import * as THREE from 'three';
import type { TweenInstance } from '../navigation/orbit/OrbitEngine';

export class View {
  position: THREE.Vector3;
  yaw: number;
  roll: number;
  radius: number;
  maxPitch: number;
  minPitch: number;
  private _pitch: number;

  /**
   * In-flight camera-motion tweens animating THIS view (setView, Utils.moveTo,
   * viewer.zoomTo). Registered so view switches can cancel them — an
   * unregistered tween keeps writing position/lookAt for up to its full
   * duration AFTER a switch, silently overwriting the new view's pose.
   */
  private motionTweens: TweenInstance[] = [];

  constructor() {
    this.position = new THREE.Vector3(0, 0, 0);

    this.yaw = Math.PI / 4;
    this._pitch = -Math.PI / 4;
    this.roll = 0;
    this.radius = 1;

    this.maxPitch = Math.PI / 2;
    this.minPitch = -Math.PI / 2;
  }

  /** Track a camera-motion tween for cancellation on view switches. */
  registerMotionTween(tween: TweenInstance): TweenInstance {
    this.motionTweens.push(tween);
    return tween;
  }

  /** Stop all in-flight camera-motion tweens targeting this view. */
  stopMotionTweens(): void {
    for (const tween of this.motionTweens) {
      tween.stop();
    }
    this.motionTweens.length = 0;
  }

  clone() {
    const c = new View();
    c.yaw = this.yaw;
    c._pitch = this.pitch;
    c.roll = this.roll;
    c.radius = this.radius;
    c.maxPitch = this.maxPitch;
    c.minPitch = this.minPitch;

    return c;
  }

  get pitch() {
    return this._pitch;
  }

  set pitch(angle) {
    this._pitch = Math.max(Math.min(angle, this.maxPitch), this.minPitch);
  }

  get direction() {
    const dir = new THREE.Vector3(0, 1, 0);

    dir.applyAxisAngle(new THREE.Vector3(1, 0, 0), this.pitch);
    dir.applyAxisAngle(new THREE.Vector3(0, 0, 1), this.yaw);

    return dir;
  }

  set direction(dir) {
    // Near-vertical guard, NOT just exact zero: for a (nearly) straight
    // up/down direction the horizontal component is pure numeric noise —
    // pose round-trips through position/target at UTM-scale coordinates
    // leave ~1e-10 of residue — and deriving yaw from it via atan2 yields an
    // ARBITRARY angle. On a top-down (2D) view that junk yaw composes with
    // the roll-based heading and visibly rotates the screen.
    //
    // A vertical look direction cannot encode yaw at all, so CANONICALIZE:
    // yaw = 0, heading lives entirely on roll (see personas/orientation.ts).
    // Keeping the pre-existing yaw is just as wrong as deriving it from
    // noise — e.g. restoring the top-down pose after a wall focus would
    // otherwise retain the focused WALL's yaw and compose it with the
    // restored roll, rotating the view by a different amount per wall.
    // Callers that restore a pose re-apply their captured roll after this
    // setter runs, landing on exactly the captured orientation.
    const horizontal = Math.hypot(dir.x, dir.y);
    if (horizontal <= 1e-6 * Math.abs(dir.z)) {
      this.pitch = (Math.PI / 2) * Math.sign(dir.z);
      this.yaw = 0;
    } else {
      const yaw = Math.atan2(dir.y, dir.x) - Math.PI / 2;
      const pitch = Math.atan2(dir.z, horizontal);

      this.yaw = yaw;
      this.pitch = pitch;
    }
  }

  lookAt(...args: number[] | [THREE.Vector3]) {
    let V: THREE.Vector3 | undefined = undefined;
    if (args.length === 1 && args[0] instanceof THREE.Vector3) {
      V = new THREE.Vector3().subVectors(args[0], this.position);
    } else if (args.length === 3) {
      V = new THREE.Vector3().subVectors(new THREE.Vector3(args[0] as number, args[1] as number, args[2] as number), this.position);
    }
    if (!V) return;

    const radius = V.length();

    this.radius = radius;
    //this.direction = dir; // Removed because it messes up 2D views
  }

  getPivot() {
    return new THREE.Vector3().addVectors(this.position, this.direction.multiplyScalar(this.radius));
  }

  getSide() {
    const side = new THREE.Vector3(1, 0, 0);
    side.applyAxisAngle(new THREE.Vector3(0, 0, 1), this.yaw);

    if (this.roll !== 0) {
      side.applyAxisAngle(this.direction, this.roll);
    }

    return side;
  }

  pan(x: number, y: number) {
    const dir = new THREE.Vector3(0, 1, 0);
    dir.applyAxisAngle(new THREE.Vector3(1, 0, 0), this.pitch);
    dir.applyAxisAngle(new THREE.Vector3(0, 0, 1), this.yaw);

    const side = this.getSide();
    const up = side.clone().cross(dir);
    const pan = side.multiplyScalar(x).add(up.multiplyScalar(y));

    this.position = this.position.add(pan);
  }

  translate(x: number, y: number, z: number) {
    const dir = new THREE.Vector3(0, 1, 0);
    dir.applyAxisAngle(new THREE.Vector3(1, 0, 0), this.pitch);
    dir.applyAxisAngle(new THREE.Vector3(0, 0, 1), this.yaw);

    const side = new THREE.Vector3(1, 0, 0);
    side.applyAxisAngle(new THREE.Vector3(0, 0, 1), this.yaw);

    const up = side.clone().cross(dir);

    const t = side.multiplyScalar(x).add(dir.multiplyScalar(y)).add(up.multiplyScalar(z));

    this.position = this.position.add(t);
  }

  translateWorld(x: number, y: number, z: number) {
    this.position.x += x;
    this.position.y += y;
    this.position.z += z;
  }

  setView(position: THREE.Vector3, target: number[] | THREE.Vector3, duration = 0, callback: (() => void) | null = null) {
    let endPosition: THREE.Vector3 | null = null;
    if (position instanceof Array) {
      endPosition = new THREE.Vector3(...position);
    } else if (position.x != null) {
      endPosition = position.clone();
    }
    if (!endPosition) return;

    let endTarget: THREE.Vector3 | null = null;
    if (target instanceof Array) {
      endTarget = new THREE.Vector3(...target);
    } else if (target.x != null) {
      endTarget = target.clone();
    }
    if (!endTarget) return;

    const startPosition = this.position.clone();
    const startTarget = this.getPivot();

    const easing = TWEEN.Easing.Quartic.Out;

    if (duration === 0) {
      this.position.copy(endPosition);
      this.lookAt(endTarget);
    } else {
      const value = { x: 0 };
      const tween = this.registerMotionTween(new TWEEN.Tween(value).to({ x: 1 }, duration));
      tween.easing(easing);

      tween.onUpdate(() => {
        if (!endPosition || !endTarget) return;
        const t = value.x;

        const pos = new THREE.Vector3((1 - t) * startPosition.x + t * endPosition.x, (1 - t) * startPosition.y + t * endPosition.y, (1 - t) * startPosition.z + t * endPosition.z);

        const target = new THREE.Vector3((1 - t) * startTarget.x + t * endTarget.x, (1 - t) * startTarget.y + t * endTarget.y, (1 - t) * startTarget.z + t * endTarget.z);

        this.position.copy(pos);
        this.lookAt(target);
      });

      tween.start();

      tween.onComplete(() => {
        if (callback) {
          callback();
        }
      });
    }
  }
}
